import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  FlaskConical,
  Inbox,
  MoreHorizontal,
  Pencil,
  Plus,
  Power,
  PowerOff,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  getCatalogoExamesControllerListarQueryKey,
  useCatalogoExamesControllerCategorias,
  useCatalogoExamesControllerDefinirAtivo,
  useCatalogoExamesControllerListar,
} from '@/api/generated/admin-catalogo-exames/admin-catalogo-exames';
import type { CatalogoExameResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { ExameFormDialogo } from '@/features/admin/catalogo-exames/components/ExameFormDialogo';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { rotaDoExameDoCatalogo, ROTAS } from '@/shared/lib/rotas';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'todos';

/**
 * Catálogo de exames (F6-3).
 *
 * ⚠️ Catálogo é GLOBAL: o que se edita aqui vale para todas as clínicas. Não há
 * exclusão — o histórico de exames aponta para cada item.
 */
export function CatalogoExamesPage() {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState<string>(TODOS);
  const [situacao, setSituacao] = useState<string>(TODOS);
  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<CatalogoExameResponse | undefined>();

  const { data, isPending, isError, refetch } = useCatalogoExamesControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'nome',
    ordem: 'asc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(categoria !== TODOS ? { categoria } : {}),
    ...(situacao !== TODOS ? { ativo: situacao as 'true' | 'false' } : {}),
  });

  const { data: categorias } = useCatalogoExamesControllerCategorias();
  const definirAtivo = useCatalogoExamesControllerDefinirAtivo();

  const podeCriar = pode('admin_catalogo_exames.criar');
  const podeEditar = pode('admin_catalogo_exames.editar');

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  async function alternarAtivo(exame: CatalogoExameResponse, ativo: boolean) {
    try {
      await definirAtivo.mutateAsync({ id: exame.id, data: { ativo } });
      await queryClient.invalidateQueries({
        queryKey: getCatalogoExamesControllerListarQueryKey(),
      });
      toast.success(ativo ? `"${exame.nome}" reativado.` : `"${exame.nome}" desativado.`);
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível alterar o exame.');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <Input
            value={busca}
            onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
            placeholder="Buscar por nome, categoria ou código..."
            className="sm:max-w-xs"
          />

          {categorias && categorias.categorias.length > 0 && (
            <Select value={categoria} onValueChange={(v) => aoFiltrar(() => setCategoria(v))}>
              <SelectTrigger className="sm:w-48" aria-label="Filtrar por categoria">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todas as categorias</SelectItem>
                {categorias.categorias.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={situacao} onValueChange={(v) => aoFiltrar(() => setSituacao(v))}>
            <SelectTrigger className="sm:w-40" aria-label="Filtrar por status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos os status</SelectItem>
              <SelectItem value="true">Ativos</SelectItem>
              <SelectItem value="false">Desativados</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to={ROTAS.adminCatalogoPendencias}>
              <Inbox className="w-4 h-4" />
              Pendências do laboratório
            </Link>
          </Button>

          {podeCriar && (
            <Button
              onClick={() => {
                setEmEdicao(undefined);
                setFormAberto(true);
              }}
            >
              <Plus className="w-4 h-4" />
              Novo exame
            </Button>
          )}
        </div>
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
            Portfólio de exames
          </h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={FlaskConical}
            titulo="Nenhum exame encontrado"
            descricao={busca ? 'Tente outro termo de busca.' : 'Cadastre o primeiro exame.'}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Exame</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Método</TableHead>
                    <TableHead>Analitos</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-20">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((exame) => (
                    <TableRow key={exame.id}>
                      <TableCell>
                        <Link
                          to={rotaDoExameDoCatalogo(exame.id)}
                          className="font-medium text-foreground hover:text-brand transition-colors"
                        >
                          {exame.nome}
                        </Link>
                        {exame.codigoVertis && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Vertis: {exame.codigoVertis}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {exame.categoria ?? '—'}
                      </TableCell>
                      {/* O método é critério de recorte da Análise Comparativa:
                          sem ele preenchido, o exame não entra em nenhum
                          recorte por método. Visível na lista para o admin ver
                          o buraco sem abrir item por item. */}
                      <TableCell className="text-muted-foreground">
                        {exame.metodoAnalitico ?? '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5">
                          {exame.totalAnalitos}
                          {/* Analito ativo sem faixa = resultado INDETERMINADO no
                              laudo. Aparece aqui para o admin achar o que falta
                              preencher sem abrir exame por exame. */}
                          {exame.analitosSemFaixa > 0 && (
                            <Badge
                              variant="outline"
                              className="text-amber-600 border-amber-500/40 gap-1 font-normal"
                              title={`${exame.analitosSemFaixa} analito(s) sem faixa de referência: o resultado sai como indeterminado.`}
                            >
                              <AlertTriangle className="w-3 h-3" />
                              {exame.analitosSemFaixa} sem faixa
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        {exame.ativo ? (
                          <Badge
                            variant="outline"
                            className="text-emerald-600 border-emerald-600/40"
                          >
                            Ativo
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-destructive border-destructive/40"
                          >
                            Desativado
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Ações de ${exame.nome}`}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {podeEditar && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setEmEdicao(exame);
                                    setFormAberto(true);
                                  }}
                                >
                                  <Pencil className="w-4 h-4" />
                                  Editar
                                </DropdownMenuItem>

                                {exame.ativo ? (
                                  <DropdownMenuItem
                                    onClick={() => void alternarAtivo(exame, false)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <PowerOff className="w-4 h-4" />
                                    Desativar
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => void alternarAtivo(exame, true)}>
                                    <Power className="w-4 h-4" />
                                    Reativar
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      <ExameFormDialogo
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        {...(emEdicao ? { exame: emEdicao } : {})}
      />
    </div>
  );
}
